<template>
  <div>
    <div class="p-6 pb-0">
      <BreadCrumbs :navigation="navigation" />
    </div>

    <div class="p-6">
      <!-- Header -->
      <div class="flex justify-between items-center mb-6">
        <div>
          <h1 class="text-2xl font-bold text-gray-800">{{ registry.name }}</h1>
          <p class="text-gray-600 text-sm mt-1">Configurazione colonne</p>
        </div>
        <div class="flex gap-2">
          <Link 
            :href="route('modules.registri-personalizzati.show', registry.id)"
            class="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md"
          >
            Visualizza Dati
          </Link>
          <button 
            @click="openColumnModal()" 
            class="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md flex items-center gap-2"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
            </svg>
            Aggiungi Colonna
          </button>
        </div>
      </div>

      <!-- Columns List -->
      <div class="bg-white rounded-lg shadow-md">
        <div class="p-4 border-b bg-gray-50">
          <h2 class="font-semibold text-gray-800">Colonne del Registro</h2>
          <p class="text-sm text-gray-600">Trascina le colonne per riordinarle</p>
        </div>

        <div v-if="localColumns.length > 0" class="divide-y">
          <div 
            v-for="(column, index) in localColumns" 
            :key="column.id"
            class="p-4 flex items-center gap-4 hover:bg-gray-50"
            draggable="true"
            @dragstart="dragStart(index)"
            @dragover.prevent
            @drop="drop(index)"
          >
            <!-- Drag Handle -->
            <div class="cursor-move text-gray-400 hover:text-gray-600">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8h16M4 16h16" />
              </svg>
            </div>

            <!-- Order Number -->
            <span class="text-gray-500 text-sm w-8">{{ index + 1 }}</span>

            <!-- Column Name -->
            <div class="flex-1">
              <span class="font-medium text-gray-800">{{ column.name }}</span>
              <span v-if="column.is_required" class="ml-2 px-2 py-0.5 bg-red-100 text-red-800 text-xs rounded-full">
                Obbligatoria
              </span>
            </div>

            <!-- Column Type -->
            <span class="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">
              {{ getTypeName(column.type) }}
            </span>

            <!-- Hidden badge -->
            <span v-if="column.is_hidden" class="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded-full">
              Nascosta
            </span>

            <!-- Value Type Preview -->
            <span class="text-gray-500 text-sm max-w-xs truncate" :title="column.value_type">
              {{ getValueTypePreview(column) }}
            </span>

            <!-- Actions -->
            <div class="flex gap-2">
              <button 
                @click="openColumnModal(column)"
                class="text-yellow-500 hover:text-yellow-600"
                title="Modifica"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
              <button 
                @click="confirmDeleteColumn(column)"
                class="text-red-500 hover:text-red-600"
                title="Elimina"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <div v-else class="p-12 text-center">
          <svg class="w-12 h-12 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p class="text-gray-500">Nessuna colonna definita. Aggiungi la prima colonna per iniziare.</p>
        </div>
      </div>
    </div>

    <!-- Column Modal -->
    <div 
      v-if="showColumnModal" 
      class="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50"
      @click="closeColumnModal"
    >
      <div class="bg-white p-6 rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto" @click.stop>
        <h2 class="text-xl font-semibold mb-4">
          {{ editingColumn ? 'Modifica Colonna' : 'Nuova Colonna' }}
        </h2>
        
        <form @submit.prevent="submitColumn">
          <!-- General Error Message (e.g., max columns reached) -->
          <div v-if="columnForm.errors.max_columns" class="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {{ columnForm.errors.max_columns }}
          </div>

          <!-- Column Name -->
          <div class="mb-4">
            <label class="block text-gray-700 text-sm font-medium mb-2">Nome Colonna *</label>
            <input 
              v-model="columnForm.name" 
              type="text" 
              class="p93__input w-full"
              placeholder="Es. Ragione Sociale"
              required
            />
            <div v-if="columnForm.errors.name" class="text-red-600 text-sm mt-1">{{ columnForm.errors.name }}</div>
          </div>

          <!-- Column Type -->
          <div class="mb-4">
            <label class="block text-gray-700 text-sm font-medium mb-2">Tipo *</label>
            <select 
              v-model="columnForm.type" 
              class="p93__input w-full"
              required
            >
              <option value="">Seleziona tipo...</option>
              <option v-for="type in availableColumnTypes" :key="type.value" :value="type.value">
                {{ type.label }}
              </option>
            </select>
            <div v-if="columnForm.errors.type" class="text-red-600 text-sm mt-1">{{ columnForm.errors.type }}</div>
          </div>

          <!-- Value Type - Only for select type -->
          <div v-if="columnForm.type === 'select'" class="mb-4">
            <label class="block text-gray-700 text-sm font-medium mb-2">Opzioni</label>
            <p class="text-sm text-gray-500 mb-2">
              Inserisci le opzioni separate da una virgola.<br>
              <span class="text-gray-400">Es.: Opzione 1, Opzione 2, Opzione 3</span>
            </p>
            <textarea 
              v-model="columnForm.value_type" 
              class="p93__input w-full"
              rows="3"
              placeholder="Opzione 1, Opzione 2, Opzione 3"
            ></textarea>
            <div v-if="columnForm.errors.value_type" class="text-red-600 text-sm mt-1">{{ columnForm.errors.value_type }}</div>
          </div>

          <!-- Value Type - Only for magoquery type (superadmin only) -->
          <div v-if="columnForm.type === 'magoquery'" class="mb-4">
            <label class="block text-gray-700 text-sm font-medium mb-2">Query SQL</label>
            <div class="text-sm text-gray-500 mb-2">
              <p>La query deve restituire esattamente <strong>due colonne</strong>:</p>
              <ul class="list-disc list-inside ml-2 mt-1">
                <li><code class="bg-gray-100 px-1 rounded">value</code> - il valore che verrà salvato</li>
                <li><code class="bg-gray-100 px-1 rounded">label</code> - il testo visualizzato all'utente</li>
              </ul>
              <p class="mt-1 text-gray-400">Es.: <code class="bg-gray-100 px-1 rounded">SELECT CustSupp AS value, CompanyName AS label FROM MA_CustSupp</code></p>
            </div>
            <textarea 
              v-model="columnForm.value_type" 
              class="p93__input w-full font-mono text-sm"
              rows="3"
              placeholder="SELECT column AS value, column AS label FROM table WHERE ..."
            ></textarea>

            <!-- SQL Query Test -->
            <div class="mt-2">
              <button 
                type="button"
                @click="testSqlQuery"
                :disabled="testingQuery"
                class="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {{ testingQuery ? 'Testing...' : 'Test Query' }}
              </button>
              
              <div v-if="queryTestResult" class="mt-2 p-2 rounded text-sm" :class="queryTestResult.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'">
                <template v-if="queryTestResult.success">
                  Query valida. {{ queryTestResult.data?.length || 0 }} risultati trovati.
                </template>
                <template v-else>
                  Errore: {{ queryTestResult.error }}
                </template>
              </div>
            </div>

            <div v-if="columnForm.errors.value_type" class="text-red-600 text-sm mt-1">{{ columnForm.errors.value_type }}</div>
          </div>

          <!-- Value Type - Only for portalquery type (superadmin only) -->
          <div v-if="columnForm.type === 'portalquery'" class="mb-4">
            <label class="block text-gray-700 text-sm font-medium mb-2">Query SQL (Database Portale)</label>
            <div class="text-sm text-gray-500 mb-2">
              <p>La query deve restituire esattamente <strong>due colonne</strong>:</p>
              <ul class="list-disc list-inside ml-2 mt-1">
                <li><code class="bg-gray-100 px-1 rounded">value</code> - il valore che verrà salvato</li>
                <li><code class="bg-gray-100 px-1 rounded">label</code> - il testo visualizzato all'utente</li>
              </ul>
              <p class="mt-1 text-gray-400">Es.: <code class="bg-gray-100 px-1 rounded">SELECT id AS value, name AS label FROM users</code></p>
            </div>
            <textarea 
              v-model="columnForm.value_type" 
              class="p93__input w-full font-mono text-sm"
              rows="3"
              placeholder="SELECT column AS value, column AS label FROM table WHERE ..."
            ></textarea>

            <!-- SQL Query Test -->
            <div class="mt-2">
              <button 
                type="button"
                @click="testPortalQuery"
                :disabled="testingQuery"
                class="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {{ testingQuery ? 'Testing...' : 'Test Query' }}
              </button>
              
              <div v-if="queryTestResult" class="mt-2 p-2 rounded text-sm" :class="queryTestResult.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'">
                <template v-if="queryTestResult.success">
                  Query valida. {{ queryTestResult.data?.length || 0 }} risultati trovati.
                </template>
                <template v-else>
                  Errore: {{ queryTestResult.error }}
                </template>
              </div>
            </div>

            <div v-if="columnForm.errors.value_type" class="text-red-600 text-sm mt-1">{{ columnForm.errors.value_type }}</div>
          </div>

          <!-- mago_customer / mago_supplier: no value_type needed, just is_hidden -->
          <div v-if="columnForm.type === 'mago_customer' || columnForm.type === 'mago_supplier'" class="mb-4 p-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-800">
            La selezione avviene per ragione sociale da MA_CustSupp. Il valore salvato è il codice CustSupp.
          </div>

          <!-- mago_custsupp_ref: ref config in value_type -->
          <div v-if="columnForm.type === 'mago_custsupp_ref'" class="mb-4">
            <label class="block text-gray-700 text-sm font-medium mb-2">Colonna di riferimento *</label>
            <select v-model="columnForm.ref_column_id" class="p93__input w-full" required>
              <option value="">Seleziona colonna cliente/fornitore...</option>
              <option
                v-for="col in custSuppParentColumns"
                :key="col.id"
                :value="col.id"
              >
                {{ col.name }} ({{ col.type === 'mago_customer' ? 'Cliente' : 'Fornitore' }})
              </option>
            </select>
            <p class="text-xs text-gray-500 mt-1">Seleziona la colonna Cliente (Mago) o Fornitore (Mago) da cui leggere il riferimento.</p>

            <label class="block text-gray-700 text-sm font-medium mb-2 mt-3">Campo MA_CustSupp *</label>
            <select
              v-model="columnForm.ref_ma_field"
              class="p93__input w-full font-mono text-sm"
              :disabled="loadingMaCustSuppColumns"
              required
            >
              <option value="">{{ loadingMaCustSuppColumns ? 'Caricamento...' : 'Seleziona campo...' }}</option>
              <option v-for="col in maCustSuppColumns" :key="col" :value="col">{{ col }}</option>
            </select>
            <p v-if="maCustSuppColumns.length === 0 && !loadingMaCustSuppColumns" class="text-xs text-yellow-600 mt-1">Nessuna colonna disponibile (connessione Mago non raggiungibile).</p>
            <p v-else class="text-xs text-gray-500 mt-1">Colonna di MA_CustSupp da leggere (escluso CustSupp già gestito dal tipo colonna).</p>
          </div>

          <!-- Width -->
          <div class="mb-4">
            <label class="block text-gray-700 text-sm font-medium mb-2">Larghezza colonna</label>
            <input
              v-model="columnForm.width"
              type="text"
              class="p93__input w-40"
              placeholder="Es. 150px, 10%"
              maxlength="20"
            />
            <p class="text-xs text-gray-400 mt-1">Lascia vuoto per larghezza automatica.</p>
            <div v-if="columnForm.errors.width" class="text-red-600 text-sm mt-1">{{ columnForm.errors.width }}</div>
          </div>

          <!-- Options -->
          <div class="flex gap-4 mb-4">
            <label v-if="columnForm.type !== 'mago_custsupp_ref'" class="flex items-center gap-2">
              <input 
                v-model="columnForm.is_required" 
                type="checkbox"
                class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span class="text-sm text-gray-700">Obbligatoria</span>
            </label>

            <label v-if="columnForm.type === 'mago_customer' || columnForm.type === 'mago_supplier'" class="flex items-center gap-2">
              <input
                v-model="columnForm.is_hidden"
                type="checkbox"
                class="rounded border-gray-300 text-yellow-600 focus:ring-yellow-500"
              />
              <span class="text-sm text-gray-700">Nascondi nella tabella</span>
            </label>
          </div>

          <div class="flex justify-end gap-2 pt-4 border-t">
            <button 
              type="button" 
              @click="closeColumnModal"
              class="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md"
            >
              Annulla
            </button>
            <button 
              type="submit" 
              :disabled="columnForm.processing"
              class="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md disabled:opacity-50"
            >
              {{ editingColumn ? 'Aggiorna' : 'Aggiungi' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div 
      v-if="showDeleteModal" 
      class="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50"
      @click="closeDeleteModal"
    >
      <div class="bg-white p-6 rounded-lg w-full max-w-md" @click.stop>
        <h2 class="text-xl font-semibold mb-4 text-red-600">Conferma Eliminazione</h2>
        <p class="text-gray-700 mb-4">
          Sei sicuro di voler eliminare la colonna "<strong>{{ columnToDelete?.name }}</strong>"? 
          Questa azione eliminerà anche tutti i valori associati.
        </p>
        
        <div class="flex justify-end gap-2">
          <button 
            type="button" 
            @click="closeDeleteModal"
            class="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md"
          >
            Annulla
          </button>
          <button 
            type="button" 
            @click="deleteColumn"
            class="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-md"
          >
            Elimina
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Link, useForm, router } from '@inertiajs/vue3';
import BreadCrumbs from '@/Components/BreadCrumbs.vue';
import axios from 'axios';

export default {
  components: {
    Link,
    BreadCrumbs
  },
  props: {
    registry: Object,
    columns: Array,
    columnTypes: Array,
  },
  data() {
    return {
      localColumns: [...(this.columns || [])],
      showColumnModal: false,
      showDeleteModal: false,
      editingColumn: null,
      columnToDelete: null,
      draggedIndex: null,
      testingQuery: false,
      queryTestResult: null,
      maCustSuppColumns: [],
      loadingMaCustSuppColumns: false,
      columnForm: useForm({
        name: '',
        type: '',
        value_type: '',
        is_required: false,
        is_hidden: false,
        width: '',
        ref_column_id: '',
        ref_ma_field: '',
      }),
      navigation: [
        { name: 'Dashboard', href: route('dashboard') },
        { name: 'Registri Personalizzati', href: route('modules.registri-personalizzati.index') },
        { name: this.registry?.name || 'Registro', href: route('modules.registri-personalizzati.show', this.registry?.id) },
        { name: 'Configura', href: '#' }
      ]
    };
  },
  computed: {
    availableColumnTypes() {
      if (!this.$page.props.isSuperAdmin) {
        return this.columnTypes?.filter(t => t.value !== 'magoquery' && t.value !== 'portalquery') || [];
      }
      return this.columnTypes || [];
    },
    isSqlQuery() {
      const vt = this.columnForm.value_type?.toUpperCase().trim() || '';
      return vt.startsWith('SELECT ');
    },

    custSuppParentColumns() {
      return this.localColumns.filter(c =>
        c.type === 'mago_customer' || c.type === 'mago_supplier'
      );
    }
  },
  methods: {
    getTypeName(type) {
      const found = this.columnTypes?.find(t => t.value === type);
      return found?.label || type;
    },
    getValueTypePreview(column) {
      if (column.type === 'mago_customer' || column.type === 'mago_supplier') {
        return '-';
      }

      if (column.type === 'mago_custsupp_ref') {
        try {
          const config = JSON.parse(column.value_type || '{}');
          const parentCol = this.localColumns.find(c => c.id == config.ref_column_id);
          const parentName = parentCol ? parentCol.name : `#${config.ref_column_id}`;
          return `${config.ma_field} → ${parentName}`;
        } catch (e) {
          return column.value_type || '-';
        }
      }

      if (!column.value_type) return '-';
      
      if (column.value_type.toUpperCase().trim().startsWith('SELECT ')) {
        return 'SQL Query';
      }
      
      try {
        const parsed = JSON.parse(column.value_type);
        if (Array.isArray(parsed)) {
          return parsed.slice(0, 3).join(', ') + (parsed.length > 3 ? '...' : '');
        }
      } catch (e) {
      }
      
      return column.value_type;
    },
    openColumnModal(column = null) {
      this.editingColumn = column;
      this.queryTestResult = null;
      
      if (column) {
        this.columnForm.name = column.name;
        this.columnForm.type = column.type;
        this.columnForm.is_required = column.is_required;
        this.columnForm.is_hidden = column.is_hidden || false;
        this.columnForm.width = column.width || '';

        if (column.type === 'mago_custsupp_ref') {
          try {
            const config = JSON.parse(column.value_type || '{}');
            this.columnForm.ref_column_id = config.ref_column_id || '';
            this.columnForm.ref_ma_field = config.ma_field || '';
          } catch (e) {
            this.columnForm.ref_column_id = '';
            this.columnForm.ref_ma_field = '';
          }
          this.columnForm.value_type = '';
          this.fetchMaCustSuppColumns();
        } else {
          let valueType = column.value_type || '';
          try {
            const parsed = JSON.parse(valueType);
            if (Array.isArray(parsed)) {
              valueType = parsed.join(', ');
            }
          } catch (e) {
          }
          this.columnForm.value_type = valueType;
          this.columnForm.ref_column_id = '';
          this.columnForm.ref_ma_field = '';
        }
      } else {
        this.columnForm.reset();
      }
      
      this.showColumnModal = true;
    },
    closeColumnModal() {
      this.showColumnModal = false;
      this.editingColumn = null;
      this.columnForm.reset();
      this.queryTestResult = null;
    },
    submitColumn() {
      let valueType = this.columnForm.value_type?.trim() || '';

      if (this.columnForm.type === 'mago_custsupp_ref') {
        if (!this.columnForm.ref_column_id || !this.columnForm.ref_ma_field?.trim()) {
          alert('Seleziona la colonna di riferimento e specifica il campo MA_CustSupp.');
          return;
        }
        valueType = JSON.stringify({
          ref_column_id: this.columnForm.ref_column_id,
          ma_field: this.columnForm.ref_ma_field.trim(),
        });
      } else if (this.columnForm.type === 'select' && valueType && !valueType.toUpperCase().startsWith('SELECT ')) {
        const options = valueType.split(',').map(opt => opt.trim()).filter(opt => opt);
        valueType = JSON.stringify(options);
      }
      
      this.columnForm.value_type = valueType;

      if (this.editingColumn) {
        this.columnForm.put(
          route('modules.registri-personalizzati.columns.update', [this.registry.id, this.editingColumn.id]),
          {
            onSuccess: () => {
              this.closeColumnModal();
            },
            preserveScroll: true
          }
        );
      } else {
        this.columnForm.post(
          route('modules.registri-personalizzati.columns.store', this.registry.id),
          {
            onSuccess: () => {
              this.closeColumnModal();
            },
            preserveScroll: true
          }
        );
      }
    },
    confirmDeleteColumn(column) {
      this.columnToDelete = column;
      this.showDeleteModal = true;
    },
    closeDeleteModal() {
      this.showDeleteModal = false;
      this.columnToDelete = null;
    },
    deleteColumn() {
      router.delete(
        route('modules.registri-personalizzati.columns.destroy', [this.registry.id, this.columnToDelete.id]),
        {
          onSuccess: () => {
            this.closeDeleteModal();
          }
        }
      );
    },
    dragStart(index) {
      this.draggedIndex = index;
    },
    drop(index) {
      if (this.draggedIndex === null || this.draggedIndex === index) return;
      
      const columns = [...this.localColumns];
      const [removed] = columns.splice(this.draggedIndex, 1);
      columns.splice(index, 0, removed);
      
      this.localColumns = columns;
      this.draggedIndex = null;
      
      const columnIds = columns.map(c => c.id);
      router.put(
        route('modules.registri-personalizzati.columns.reorder', this.registry.id),
        { column_ids: columnIds },
        { preserveScroll: true }
      );
    },
    async fetchMaCustSuppColumns() {
      if (this.maCustSuppColumns.length > 0) return;
      this.loadingMaCustSuppColumns = true;
      try {
        const response = await axios.get(route('modules.registri-personalizzati.api.ma-custsupp-columns'));
        this.maCustSuppColumns = response.data.columns || [];
      } catch (e) {
        this.maCustSuppColumns = [];
      } finally {
        this.loadingMaCustSuppColumns = false;
      }
    },
    async testSqlQuery() {
      this.testingQuery = true;
      this.queryTestResult = null;

      try {
        const response = await axios.post(route('modules.registri-personalizzati.query.test'), {
          query: this.columnForm.value_type
        });
        this.queryTestResult = response.data;
      } catch (error) {
        this.queryTestResult = {
          success: false,
          error: error.response?.data?.error || error.message
        };
      } finally {
        this.testingQuery = false;
      }
    },
    async testPortalQuery() {
      this.testingQuery = true;
      this.queryTestResult = null;

      try {
        const response = await axios.post(route('modules.registri-personalizzati.query.portal.test'), {
          query: this.columnForm.value_type
        });
        this.queryTestResult = response.data;
      } catch (error) {
        this.queryTestResult = {
          success: false,
          error: error.response?.data?.error || error.message
        };
      } finally {
        this.testingQuery = false;
      }
    }
  },
  watch: {
    columns: {
      handler(newColumns) {
        this.localColumns = [...(newColumns || [])];
      },
      deep: true
    },
    'columnForm.type'(newType) {
      if (['text', 'number', 'date', 'mago_customer', 'mago_supplier', 'hyperlink'].includes(newType)) {
        this.columnForm.value_type = '';
      }
      if (newType !== 'mago_custsupp_ref') {
        this.columnForm.ref_column_id = '';
        this.columnForm.ref_ma_field = '';
      } else {
        this.fetchMaCustSuppColumns();
      }
    }
  }
};
</script>

